import { CatalogItem } from '../CatalogItem';
import { isSizeEqual } from '../CatalogItemSize';
import { CatalogItemWithKey, convertToDefault } from '../CatalogItemWithKey';
import { CatalogFilterCondition } from './CatalogFilterCondition';
import { findMatchColor } from './findMatchColor';
import { isRegionItemOnSale } from './isRegionItemOnSale';
import { matchCategory } from './matchCategory';

export function filterCatalogItems(
  condition: CatalogFilterCondition,
  items: CatalogItem[],
  isOpenAndEnterRemakeShop?: boolean,
  isUnlockFenceRemake?: boolean
): CatalogItemWithKey[] {
  const results: CatalogItemWithKey[] = [];
  for (const item of items) {
    if (!matchCategory(condition, item)) {
      continue;
    }

    // 非売品のアイテムが価格の条件検索に引っかかることを防ぐ
    if (
      (condition.minPrice !== undefined || condition.maxPrice !== undefined) &&
      !canBuyFromCatalog(item)
    ) {
      continue;
    }
    if (condition.minPrice !== undefined && condition.minPrice > item.price) {
      continue;
    }
    if (condition.maxPrice !== undefined && condition.maxPrice < item.price) {
      continue;
    }
    if (!matchCatalogType(condition, item)) {
      continue;
    }
    if (!matchRemakable(condition, item, isOpenAndEnterRemakeShop, isUnlockFenceRemake)) {
      continue;
    }

    if (!matchSize(condition, item)) {
      continue;
    }

    if (condition.color) {
      const found = findMatchColor(condition.color, item);
      if (found) {
        results.push(found);
      } else {
        continue;
      }
    } else {
      const converted = convertToDefault(item);
      results.push(converted);
    }
  }

  return results;
}

function matchCatalogType(condition: CatalogFilterCondition, item: CatalogItem): boolean {
  if (!condition.catalogType) {
    return true;
  }
  const canBuy = canBuyFromCatalog(item);
  switch (condition.catalogType) {
    case 'Sale':
      return canBuy;
    case 'NotForSale':
      return canBuy === false;
    case 'NotSee':
    default:
      // NotSeeはconditionに設定されないはずであるため、条件が設定されていない時と同じ扱いをする。
      return true;
  }
}

// catalog_typeとfromから、カタログから購入可能か判別する関数
// https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=940164196
// https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=940164196&focusedCommentId=1043016638#comment-1043016638
function canBuyFromCatalog(item: CatalogItem): boolean {
  switch (item.catalog_type) {
    case 'Sale':
      return true;
    case 'NotForSale':
      return false;
    case 'NotSee':
      if (item.from === 'collaborate') {
        return true;
      } else if (item.from === 'region') {
        return !!item.region_event && isRegionItemOnSale(item.region_event.period);
      } else {
        return false;
      }
  }
}

function matchRemakable(
  condition: CatalogFilterCondition,
  item: CatalogItem,
  isOpenAndEnterRemakeShop?: boolean,
  isUnlockFenceRemake?: boolean
): boolean {
  if (!condition.remakable) {
    return true;
  }

  if (item.kind_id === 'Fence') {
    const isFenceRemakable = !!isUnlockFenceRemake && item.remakable;

    if (isOpenAndEnterRemakeShop) {
      // 柵リメイクを解禁していなくても、カイゾーリメイクが可能な場合はリメイク可能と判定する
      // reference: https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=1060506959#id-%E3%80%90Blanco%E3%80%91%E3%82%AB%E3%82%BF%E3%83%AD%E3%82%B0%E6%A9%9F%E8%83%BD[v2.0.0]-%E6%9F%B5%E3%83%AA%E3%83%A1%E3%82%A4%E3%82%AF%E3%81%AB%E3%81%A4%E3%81%84%E3%81%A6
      const remakable = !!item.shop_remakable || isFenceRemakable;

      switch (condition.remakable) {
        case 'yes':
          return remakable;
        case 'no':
        default:
          return !remakable;
      }
    } else {
      switch (condition.remakable) {
        case 'yes':
          return isFenceRemakable;
        case 'no':
        default:
          return !isFenceRemakable;
      }
    }
  } else {
    const isItemRemakable = item.remakable;

    if (isOpenAndEnterRemakeShop) {
      const remakable = isItemRemakable || !!item.shop_remakable;

      switch (condition.remakable) {
        case 'yes':
          return remakable;
        case 'no':
        default:
          return !remakable;
      }
    } else {
      switch (condition.remakable) {
        case 'yes':
          return isItemRemakable;
        case 'no':
        default:
          return !isItemRemakable;
      }
    }
  }
}

function matchSize(condition: CatalogFilterCondition, item: CatalogItem): boolean {
  if (!condition.size) {
    return true;
  }
  return isSizeEqual(condition.size, item);
}
